import { lazy, Suspense } from 'react';

import { Loading } from '../../components/ui.jsx';

/**
 * Accounts in the web app: client login, sign-up, password reset, email verification and the 2FA
 * step (bare pages), the staff login, the client's security page, and staff, roles and "my account"
 * in the admin area. Every page is its own lazy chunk.
 */
const page = (load, name) => () => load().then((m) => ({ Component: m[name] }));
const login = () => import('./Login.jsx');
const register = () => import('./Register.jsx');
const password = () => import('./Password.jsx');
const adminLogin = () => import('./AdminLogin.jsx');
const security = () => import('./Security.jsx');
const staff = () => import('./Staff.jsx');
const roles = () => import('./Roles.jsx');
const account = () => import('./AdminAccount.jsx');
const impersonation = () => import('./Impersonation.jsx');

const deferred = (load, name) => {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] })));
  return function Deferred(props) {
    return (
      <Suspense fallback={<Loading />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
};

export default {
  name: 'auth',
  publicRoutes: [
    { path: 'login', bare: true, lazy: page(login, 'Login') },
    { path: '2fa', bare: true, lazy: page(login, 'TwoFactor') },
    { path: 'register', bare: true, lazy: page(register, 'Register') },
    { path: 'password/forgot', bare: true, lazy: page(password, 'Forgot') },
    { path: 'password/reset', bare: true, lazy: page(password, 'Reset') },
    { path: 'verify-email', bare: true, lazy: page(password, 'VerifyEmail') },
  ],
  clientRoutes: [{ path: 'account/security', lazy: page(security, 'ClientSecurity') }],
  adminRoutes: [
    { path: 'login', bare: true, lazy: page(adminLogin, 'AdminLogin') },
    { path: 'password/forgot', bare: true, lazy: page(password, 'StaffForgot') },
    { path: 'password/reset', bare: true, lazy: page(password, 'Reset') },
    { path: 'staff', lazy: page(staff, 'StaffList') },
    { path: 'staff/:uuid', lazy: page(staff, 'StaffDetail') },
    { path: 'roles', lazy: page(roles, 'RolesList') },
    { path: 'roles/:id', lazy: page(roles, 'RoleDetail') },
    { path: 'account', lazy: page(account, 'AdminAccount') },
  ],
  clientNav: [{ key: 'security', to: '/account/security', order: 90, auth: true }],
  adminNav: [
    { section: 'staff', to: '/admin/staff', label: 'Staff', permission: 'admin.staff.read', order: 10 },
    { section: 'staff', to: '/admin/roles', label: 'Roles', permission: 'admin.roles.manage', order: 20 },
  ],
  clientWidgets: [
    { key: 'impersonation', order: 0, wide: true, Component: deferred(impersonation, 'ImpersonationWidget') },
  ],
  adminClientTabs: [
    {
      key: 'access',
      label: 'View as client',
      order: 90,
      permission: 'admin.clients.impersonate',
      Component: deferred(impersonation, 'ClientAccessTab'),
    },
  ],
};
